import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Twitch, ExternalLink, RefreshCw, X, Check } from "lucide-react";
import { toast } from "sonner";
import { useOAuth } from "@/hooks/useOAuth";
import { useOAuthPersistence } from "@/hooks/useOAuthPersistence";

interface TwitchConnectionProps {
	value: string;
	onChange: (value: string) => void;
	connected: boolean;
	onConnect: (username?: string) => void;
	onDisconnect: () => void;
	onRefresh?: () => void;
	loading?: boolean;
}

export function TwitchConnection({
	value,
	onChange,
	connected,
	onConnect,
	onDisconnect,
	onRefresh,
	loading = false
}: TwitchConnectionProps) {
	const [isPlatformConfigured, setIsPlatformConfigured] = useState(false);
	const { isConnecting, connect, disconnect, isPlatformConfigured: checkPlatform } = useOAuth();
	const { isConnected: isPersistedConnected, getConnection, disconnect: persistDisconnect } = useOAuthPersistence();
	const [isRefreshing, setIsRefreshing] = useState(false);

	useEffect(() => {
		setIsPlatformConfigured(checkPlatform('twitch'));
	}, [checkPlatform]);

	// Load persisted connection on mount
	useEffect(() => {
		if (isPersistedConnected('twitch') && !connected) {
			const connection = getConnection('twitch');
			if (connection) {
				onConnect(connection.userProfile.username);
			}
		}
	}, [isPersistedConnected, getConnection, onConnect, connected]);

	useEffect(() => {
		const handleOAuthConnected = (event: CustomEvent) => {
			const { platform, userProfile } = event.detail;
			if (platform === 'twitch' && !connected) {
				onConnect(userProfile.username);
			}
		};

		const handleOAuthDisconnected = (event: CustomEvent) => {
			const { platform } = event.detail;
			if (platform === 'twitch' && connected) {
				onDisconnect();
			}
		};

		window.addEventListener('oauth_connected', handleOAuthConnected as EventListener);
		window.addEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);

		return () => {
			window.removeEventListener('oauth_connected', handleOAuthConnected as EventListener);
			window.removeEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);
		};
	}, [onConnect, onDisconnect, connected]);

	const handleConnect = async () => {
		if (!isPlatformConfigured) {
			toast.error("Twitch OAuth is not configured. Please check your environment variables.");
			return;
		}

		try {
			await connect('twitch');
		} catch (error) {
			console.error("Twitch connection error:", error);
			// Error is already handled in the hook
		}
	};


	const handleDisconnect = () => {
		persistDisconnect('twitch');
		onDisconnect();
	};

	const handleRefresh = async () => {
		if (onRefresh) {
			setIsRefreshing(true);
			await onRefresh();
			setIsRefreshing(false);
		}
	};

	const connection = getConnection('twitch');
	// Username fallback if connection object is missing but 'connected' prop is true (legacy fallback)
	const displayUsername = connection?.userProfile.displayName || value || "twitch_user";
	const avatarUrl = connection?.userProfile.avatarUrl;
	const followersCount = connection?.userProfile.followersCount || 0;
	const subscribersCount = connection?.userProfile.subscribersCount || 0;
	const viewsCount = connection?.userProfile.viewsCount || 0;

	return (
		<div>
			<Label htmlFor="twitch" className="flex items-center gap-2 mb-1">
				<Twitch className="w-4 h-4" />
				Twitch
			</Label>
			{connected ? (
				<div className="space-y-3">
					<div className="flex items-center gap-3 p-3 bg-muted/50 rounded-custom-paddings">
						<button
							onClick={() => {
								const username = connection?.userProfile.username || value?.replace('https://twitch.tv/', '');
								window.open(`https://twitch.tv/${username}`, '_blank');
							}}
							className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity text-left"
						>
							<Avatar className="w-8 h-8">
								<AvatarImage src={avatarUrl} alt={displayUsername} />
								<AvatarFallback>
									<Twitch className="w-4 h-4" />
								</AvatarFallback>
							</Avatar>
							<div className="flex-1 overflow-hidden">
								<p className="font-medium text-sm truncate">{displayUsername}</p>
								<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
									{connection?.userProfile.verified && (
										<Badge variant="secondary" className="h-4 text-[10px] px-1 font-normal">
											Verified
										</Badge>
									)}
									{loading ? (
										<span className="text-[10px] text-muted-foreground animate-pulse">Loading stats...</span>
									) : (
										<>
											{followersCount > 0 && (
												<div className="text-[10px] text-muted-foreground whitespace-nowrap">
													{followersCount.toLocaleString()} followers
												</div>
											)}
											{subscribersCount > 0 && (
												<div className="text-[10px] text-muted-foreground whitespace-nowrap">
													• {subscribersCount.toLocaleString()} subs
												</div>
											)}
											{viewsCount > 0 && (
												<div className="text-[10px] text-muted-foreground whitespace-nowrap">
													• {viewsCount.toLocaleString()} views
												</div>
											)}
										</>
									)}
								</div>
							</div>
						</button>

						<div className="flex items-center gap-1">
							{onRefresh && (
								<Button
									variant="ghost"
									size="icon"
									onClick={handleRefresh}
									disabled={loading || isRefreshing}
									className="h-8 w-8 text-muted-foreground hover:text-foreground"
									title="Refresh stats"
								>
									<RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
								</Button>
							)}
							<Button
								variant="destructive"
								size="sm"
								onClick={handleDisconnect}
								disabled={loading}
								className="h-8 px-2"
							>
								<X className="w-4 h-4" />
							</Button>
						</div>
					</div>
				</div>
			) : (
				<div className="space-y-2">
					<p className="text-sm text-muted-foreground">
						Connect your Twitch account to automatically retrieve your information
					</p>
					{isPlatformConfigured ? (
						<Button
							onClick={handleConnect}
							disabled={isConnecting || loading}
							variant="outline"
							className="w-full"
						>
							{isConnecting ? "Connecting..." : "Connect with Twitch"}
						</Button>
					) : (
						<div className="text-center p-2 border border-yellow-200 rounded-md bg-yellow-50">
							<p className="text-xs text-yellow-600">
								Twitch OAuth is not configured
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
